import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { UserCompanyAccessRequest } from '@interfaces/economic-groups/requests/economic-group.request';
import { EconomicGroupsHttpService } from '@services/http/economic-groups/economic-groups-http.service';
import { CompanyService } from '@services/http/company.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { TranslateService } from '@ngx-translate/core';
import { Option } from '@interfaces/common/option';
import { catchError, finalize, map } from 'rxjs/operators';
import { of } from 'rxjs';

type AccessMode = 'none' | 'single' | 'group' | 'multi';

@Component({
  selector: 'app-user-access-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule],
  templateUrl: './user-access-panel.component.html',
  styleUrls: ['./user-access-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserAccessPanelComponent extends VaultBaseComponent implements OnInit, OnChanges {
  /**
   * ID do usuário sendo editado. Ao mudar, recarrega o acesso atual.
   * Fica indefinido no modo criação — o usuário ainda não existe, então não há
   * escopo para carregar nem `PUT /users/{id}/company-access` para chamar.
   */
  @Input() userId?: string;

  /**
   * `true` (padrão, modo edição): o painel salva sozinho pelo botão "Salvar escopo".
   * `false` (modo criação): o painel vira só um seletor — esconde o botão e deixa o
   * componente pai persistir o escopo com `buildAccessPayload()` logo após criar o
   * usuário, quando o `userId` finalmente existe.
   */
  @Input() autoSave = true;

  /**
   * `true` quando o usuário editado é um parceiro comercial.
   *
   * **Desde o Card A do vínculo Parceiro-Empresa (card 2900), o vínculo mudou de dono.**
   * Este painel deixou de oferecer qualquer seletor para o parceiro: ele só mostra um aviso
   * apontando para **Gerenciamento › Parceiros**, aba Empresas — é lá que o escopo de
   * empresas do parceiro inteiro (todos os usuários dele) é definido, com a origem "Deal"
   * ou "Manual" de cada empresa.
   *
   * Antes desta mudança, o painel entrava num modo "empresas avulsas" que **somava** ao que
   * os deals do parceiro já concediam. Esse caminho foi desativado porque, depois da
   * migration do backend, o vínculo manual por usuário deixou de existir: o `PUT` daqui
   * mostraria lista vazia e recusaria salvar, sem explicar por quê — pior do que não
   * oferecer o seletor.
   */
  @Input() partnerMode = false;

  readonly loading = signal(false);
  readonly saving = signal(false);

  mode: AccessMode = 'none';
  selectedGroupId: string | null = null;
  selectedCompanyId: string | null = null;
  selectedCompanyIds: string[] = [];

  groupOptions: Option[] = [];
  companyOptions: Option[] = [];

  constructor(
    translateService: TranslateService,
    private readonly accessService: EconomicGroupsHttpService,
    private readonly companyService: CompanyService,
    private readonly toast: ToastHelperService,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef
  ) {
    super(translateService);
  }

  ngOnInit(): void {
    // No modo parceiro o painel só mostra o aviso — nenhum template renderiza os seletores de
    // empresa/grupo econômico que `loadOptions` preenche, então chamá-lo aqui seria duas
    // requisições jogadas fora a cada abertura. Mesmo espírito da guarda do `ngOnChanges`.
    if (!this.partnerMode) {
      this.loadOptions();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // No modo parceiro o painel só mostra o aviso — não há seletor para preencher, e desde
    // a migration do vínculo (card 2900) o vínculo manual por usuário deixou de existir.
    // Chamar `getUserAccess` aqui só arriscaria um toast de erro sem UI nenhuma para explicá-lo.
    if (changes['userId']?.currentValue && !this.partnerMode) {
      this.loadCurrentAccess();
    }
  }

  save(): void {
    if (!this.userId) return;

    this.saving.set(true);

    const payload = this.buildAccessPayload();

    this.accessService
      .setUserAccess(this.userId, payload)
      .pipe(
        finalize(() => {
          this.saving.set(false);
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => this.toast.showToastSuccess('Escopo de acesso atualizado com sucesso.'),
        error: () => this.toast.showToastError('Erro ao atualizar escopo de acesso.'),
      });
  }

  /**
   * Monta o payload do escopo a partir do modo selecionado.
   * Público para que o cadastro de usuário consiga persistir o escopo escolhido na
   * criação, logo depois que o usuário passa a existir.
   */
  buildAccessPayload(): UserCompanyAccessRequest {
    switch (this.mode) {
      case 'group':
        return { economicGroupId: this.selectedGroupId ?? undefined };
      case 'single':
        return { companyId: this.selectedCompanyId ?? undefined };
      case 'multi':
        return { companyIds: this.selectedCompanyIds };
      default:
        return {};
    }
  }

  /**
   * Indica se o usuário realmente escolheu um escopo utilizável.
   * Evita disparar um `PUT` vazio quando o modo foi marcado mas nada foi selecionado.
   */
  hasScopeSelection(): boolean {
    switch (this.mode) {
      case 'group':
        return !!this.selectedGroupId;
      case 'single':
        return !!this.selectedCompanyId;
      case 'multi':
        return this.selectedCompanyIds.length > 0;
      default:
        return false;
    }
  }

  private loadCurrentAccess(): void {
    if (!this.userId) return;
    this.loading.set(true);

    this.accessService
      .getUserAccess(this.userId)
      .pipe(
        finalize(() => {
          this.loading.set(false);
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: access => {
          if (!access) return;
          if (access.economicGroupId) {
            this.mode = 'group';
            this.selectedGroupId = access.economicGroupId;
          } else if (access.companyId) {
            this.mode = 'single';
            this.selectedCompanyId = access.companyId;
          } else if (access.companyIds?.length) {
            this.mode = 'multi';
            this.selectedCompanyIds = access.companyIds;
          } else {
            this.mode = 'none';
          }
          this.cdr.markForCheck();
        },
        error: () => this.toast.showToastError('Erro ao carregar acesso do usuário.'),
      });
  }

  private loadOptions(): void {
    this.accessService
      .getFiltered({ pageSize: 100, page: 1 })
      .pipe(
        map(res => (res?.data ?? []).map(g => ({ label: g.name, value: g.id }))),
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(opts => {
        this.groupOptions = opts;
        this.cdr.markForCheck();
      });

    this.companyService
      .getAllCompanys()
      .pipe(
        map(res =>
          (res?.data ?? []).filter(c => c.id).map(c => ({ label: c.corporateName, value: c.id! }))
        ),
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(opts => {
        this.companyOptions = opts;
        this.cdr.markForCheck();
      });
  }
}
